import { useState } from 'react';
import { Button, Select, SelectItem, Checkbox } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import FileUploadField from '../../base/FileUploadField';
import { AcceptedFileType, FileUploadTabType } from '../../base/file-upload/utils';
import { FileMetadata } from '@/services/apis/files';
import { ContentDepth } from '@/services/apis/guides';
import PdfConversionGuide from './PdfConversionGuide';

interface GenerateGuideFromPdfProps {
  onSubmit: (fileId: string, processImages: boolean, contentDepth: ContentDepth) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const GenerateGuideFromPdf: React.FC<GenerateGuideFromPdfProps> = ({ onSubmit, onCancel, isSubmitting = false }) => {
  const { t } = useTranslation('admin');
  const [fileMetadata, setFileMetadata] = useState<FileMetadata | null>(null);
  const [processImages, setProcessImages] = useState<boolean>(false);
  const [contentDepth, setContentDepth] = useState<ContentDepth>(ContentDepth.SIMPLIFIED);

  const handleFileUploaded = (metadata: FileMetadata) => {
    setFileMetadata(metadata);
  };

  const handleRemoveFile = () => {
    setFileMetadata(null);
  };

  const handleSubmit = () => {
    if (fileMetadata?.id) {
      onSubmit(fileMetadata.id, processImages, contentDepth);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className="space-y-4"
    >
      <FileUploadField
        file={null}
        onChange={() => {}}
        acceptedTypes={[AcceptedFileType.PDF]}
        maxSize={10}
        shownTabs={[FileUploadTabType.FILE_UPLOAD, FileUploadTabType.MEDIA_LIBRARY, FileUploadTabType.EXTERNAL_URL]}
        onFileUploaded={handleFileUploaded}
        selectedFileId={fileMetadata?.id}
      />

      <div className="space-y-4 pt-4">
        <Select
          label={t('guides.modal.pdfImport.contentDepth.label', { defaultValue: 'Content Depth' })}
          selectedKeys={[contentDepth]}
          onSelectionChange={(keys) => {
            const selected = Array.from(keys)[0] as ContentDepth;
            setContentDepth(selected);
          }}
          defaultSelectedKeys={[ContentDepth.SIMPLIFIED]}
        >
          <SelectItem key={ContentDepth.SIMPLIFIED}>
            {t('guides.modal.pdfImport.contentDepth.simplified', {
              defaultValue: 'Simplified - easy to understand',
            })}
          </SelectItem>
          <SelectItem key={ContentDepth.TECHNICAL}>
            {t('guides.modal.pdfImport.contentDepth.technical', {
              defaultValue: 'Technical - Professional level',
            })}
          </SelectItem>
          <SelectItem key={ContentDepth.IN_DEPTH}>
            {t('guides.modal.pdfImport.contentDepth.inDepth', {
              defaultValue: 'In-depth - Comprehensive coverage',
            })}
          </SelectItem>
        </Select>

        <Checkbox isSelected={processImages} onValueChange={setProcessImages} size="md">
          {t('guides.modal.pdfImport.processImages', {
            defaultValue: 'Process images (slower but includes visuals)',
          })}
        </Checkbox>
      </div>
      {fileMetadata && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center justify-between">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium text-blue-700 dark:text-blue-300">Selected file:</span>{' '}
            <span className="text-gray-900 dark:text-gray-100">{fileMetadata.name}</span>
          </p>
          <button
            type="button"
            onClick={handleRemoveFile}
            className="ml-2 p-1 hover:bg-blue-100 dark:hover:bg-blue-800 rounded transition-colors"
            aria-label="Remove selected file"
          >
            <X className="w-4 h-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100" />
          </button>
        </div>
      )}

      <PdfConversionGuide />

      <div className="flex justify-end gap-3 pt-6">
        <Button type="button" variant="light" onPress={onCancel} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" color="primary" isLoading={isSubmitting} isDisabled={!fileMetadata?.id}>
          {t('guides.modal.generateButton', { defaultValue: 'Generate Guide' })}
        </Button>
      </div>
    </form>
  );
};

GenerateGuideFromPdf.displayName = 'GenerateGuideFromPdf';

export default GenerateGuideFromPdf;
